import { type JSONConfig, MeasurementKind, TuneControlsKind, GroupKind } from '@/engine/app/interfaces.ts';
import { viewPorts } from './_common.config.ts';
import * as defaultViewOptionsGroups from './_viewOptionsGroup.config.ts';
import * as defaultObjectModel from './_viewObjects.config.ts';
import * as defaultViewOptionsModel from './_viewOptions.config.ts';
import { heartWorkflowIndications } from './_wfIndications.config.ts';
import { axial, coronal, sagittal } from '@/engine/common/imageSlices.config.ts';
import { SliceType } from '@/engine/app/modules/images/interfaces.ts';

export const config: JSONConfig.View = {
  descriptor: 'delivery',
  label: 'Delivery',
  sideLabel: 'Delivery',
  imageStackDescriptor: 'Heart',
  imagesRoiConfig: {
    descriptor: 'device_roi',
    syncSliceDescriptor: SliceType.SAGITTAL,
    zoomFactor: 0.5
  },
  viewOrientationDescriptor: 'heart_orientation',
  viewOptions: [
    {
      ...defaultViewOptionsGroups.devices,
      children: [
        defaultViewOptionsModel.valve,
        defaultViewOptionsModel.catheter,
        defaultViewOptionsModel.additionalDevice,
        defaultViewOptionsModel.additionalStructure
      ]
    },
    {
      ...defaultViewOptionsGroups.rightHeart,
      children: [
        defaultViewOptionsModel.rightAtrium,
        defaultViewOptionsModel.rightVentricle,
        defaultViewOptionsModel.rightHeart,
        defaultViewOptionsModel.pulmonaryArtery,
        defaultViewOptionsModel.calcification
      ]
    },
    {
      ...defaultViewOptionsGroups.leftHeart,
      children: [
        defaultViewOptionsModel.leftAtrium,
        defaultViewOptionsModel.leftVentricle,
        defaultViewOptionsModel.aorta
      ]
    }
  ],
  objects: [
    { ...defaultObjectModel.landingZoneSpline, isVisible: true },
    defaultObjectModel.valve,
    defaultObjectModel.catheter,
    defaultObjectModel.leftAtrium,
    defaultObjectModel.leftVentricle,
    defaultObjectModel.rightAtrium,
    defaultObjectModel.rightVentricle,
    defaultObjectModel.rightHeart,
    { ...defaultObjectModel.aorta, isVisible: false },
    defaultObjectModel.pulmonaryArtery,
    {
      ...defaultObjectModel.eustachianRidgeSpline,
      isVisible: true,
      isPickable: true,
      isTunable: true,
      isEditingActivated: true
    },
    { ...defaultObjectModel.raHeightPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.rvDepthCoaxialPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.rvDepthApexPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvAPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvPPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvSPoint, isPickable: true, isTunable: true },
    { ...defaultObjectModel.basalRvTvLPoint, isPickable: true, isTunable: true },
    defaultObjectModel.additionalDevice,
    { ...defaultObjectModel.additionalStructure, isContourVisible: true },
    defaultObjectModel.veinsCalcification,
    defaultObjectModel.calcification
  ],
  centerLines: [
    {
      descriptor: 'long_axis.centerline',
      isVisible: false
    }
  ],
  measurements: [
    {
      descriptor: 'landing_zone_area',
      isPickable: false,
      isTunable: false,
      isVisible: true
    },
    {
      descriptor: 'var_basal_rv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_tv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_tv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'tp.pl_svc1.area',
      isVisible: false,
      isPickable: true,
      isTunable: true
    },
    {
      descriptor: 'tp.pl_ivc1.area',
      isPickable: true,
      isTunable: true
    },
    {
      descriptor: 'var_catheter_primary_flex',
      isVisible: false
    },
    {
      descriptor: 'var_catheter_secondary_flex',
      isVisible: false
    },
    {
      descriptor: 'var_catheter_rotation_angle',
      isVisible: false
    },
    {
      descriptor: 'var_catheter_to_tv_angle',
      isVisible: false
    },
    {
      descriptor: 'var_ra_height',
      isTunable: true,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth',
      isTunable: true,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth_apex',
      isTunable: true,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva_offset',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ra_rv_working_room'
    },
    {
      descriptor: 'var_ra_volume'
    },
    {
      descriptor: 'var_rv_volume'
    }
  ],
  measurementBoxes: [
    {
      kind: MeasurementKind.Distance,
      label: 'Working room',
      hasCriteria: true,
      groupDescriptor: 'working.room',
      entries: ['var_ivc_to_tva_offset', 'var_ivc_to_tva', 'var_ra_height', 'var_rv_depth', 'var_rv_depth_apex']
    },
    {
      kind: MeasurementKind.Value,
      label: '',
      hasCriteria: true,
      groupDescriptor: 'working.room',
      entries: ['var_ra_rv_working_room']
    },
    {
      kind: MeasurementKind.Volume,
      label: 'Right Heart Volumes',
      groupDescriptor: 'working.room',
      order: 2,
      entries: ['var_ra_volume', 'var_rv_volume']
    },
    {
      kind: MeasurementKind.Area,
      order: 2,
      groupDescriptor: 'working.room',
      label: 'Right Atrium Ostium Measurements',
      entries: ['tp.pl_ivc1.area', 'tp.pl_svc1.area']
    },
    {
      kind: MeasurementKind.Angle,
      groupDescriptor: 'catheter',
      label: 'Delivery Angles',
      order: 1,
      entries: [
        'var_catheter_primary_flex',
        'var_catheter_secondary_flex',
        'var_catheter_rotation_angle',
        'var_catheter_to_tv_angle'
      ]
    },
    {
      kind: MeasurementKind.Distance,
      groupDescriptor: 'basal.rv',
      label: 'Basal RV custom',
      entries: ['var_basal_rv_tv_ap', 'var_basal_rv_tv_sl']
    }
  ],
  objectBoxes: [],
  workflowIndications: [...heartWorkflowIndications],
  orientationPresets: [],
  tuneControls: [
    {
      groupDescriptor: 'basal.rv',
      kind: TuneControlsKind.Stepper,
      label: 'Custom plane depth',
      units: {
        value: 'mm'
      },
      descriptor: 'tp.basal_rv_tv.translation',
      locatableDescriptors: ['var_basal_rv_tv_ap', 'var_basal_rv_tv_sl'],
      hasReset: true
    },
    {
      kind: TuneControlsKind.Reset,
      label: 'Reset catheter position',
      groupDescriptor: 'catheter',
      descriptor: [
        'tp.catheterPrimaryRotation',
        'tp.catheterSecondaryRotation',
        'tp.catheterZRotation',
        'tp.catheterZTranslation'
      ]
    },
    {
      kind: TuneControlsKind.Stepper,
      label: 'Primary flex',
      groupDescriptor: 'catheter',
      units: {
        value: '°'
      },
      descriptor: 'tp.catheterPrimaryRotation'
    },
    {
      kind: TuneControlsKind.Stepper,
      groupDescriptor: 'catheter',
      label: 'Secondary flex',
      units: {
        value: '°'
      },
      descriptor: 'tp.catheterSecondaryRotation'
    },
    {
      kind: TuneControlsKind.Stepper,
      groupDescriptor: 'catheter',
      label: 'Rotation',
      units: {
        value: '°'
      },
      descriptor: 'tp.catheterZRotation'
    },
    {
      kind: TuneControlsKind.Stepper,
      groupDescriptor: 'catheter',
      label: 'Translation',
      units: {
        value: 'mm'
      },
      descriptor: 'tp.catheterZTranslation'
    }
  ],
  planningEntitiesGroups: [
    {
      descriptor: 'working.room',
      label: 'Working room',
      isExpanded: true,
      kind: GroupKind.Collapsible
    },
    {
      descriptor: 'catheter',
      label: 'Catheter',
      order: 2,
      kind: GroupKind.Collapsible
    },
    {
      descriptor: 'basal.rv',
      label: 'Basal RV',
      order: 1,
      kind: GroupKind.Collapsible
    }
  ],
  imageSlices: [axial, coronal, sagittal],
  viewPorts,
  visibilityDependencies: [],
  selectionDependencies: [
    {
      item: 'tp.pt_ra_height',
      dependsOn: ['var_ra_height']
    },
    {
      item: 'tp.pt_rv_depth',
      dependsOn: ['var_rv_depth']
    },
    {
      item: 'tp.pt_rv_depth_apex',
      dependsOn: ['var_rv_depth_apex']
    },
    {
      item: 'tp.pt_basal_rv_tv_a',
      dependsOn: ['var_basal_rv_tv_ap']
    },
    {
      item: 'tp.pt_basal_rv_tv_p',
      dependsOn: ['var_basal_rv_tv_ap']
    },
    {
      item: 'tp.pt_basal_rv_tv_s',
      dependsOn: ['var_basal_rv_tv_sl']
    },
    {
      item: 'tp.pt_basal_rv_tv_l',
      dependsOn: ['var_basal_rv_tv_sl']
    }
  ],
  helpers: []
};
